import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, MapPin, Plus, Loader2, ExternalLink, CheckCircle, AlertTriangle, Tag } from 'lucide-react';

const STATUS_STYLES = {
  active:          'bg-green-100 text-green-800 border-green-200',
  trial:           'bg-amber-100 text-amber-800 border-amber-200',
  pending_payment: 'bg-blue-100 text-blue-800 border-blue-200',
  past_due:        'bg-orange-100 text-orange-800 border-orange-200',
  canceled:        'bg-red-100 text-red-700 border-red-200',
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocName, setNewLocName] = useState('');
  const [showAddLocation, setShowAddLocation] = useState(false);

  const refresh = async () => {
    const [b, l] = await Promise.all([axios.get(`${API}/stripe/billing`), axios.get(`${API}/locations`)]);
    setBilling(b.data);
    setLocations(l.data.filter(loc => loc.active));
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/customer-portal`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open billing portal');
    } finally { setPortalLoading(false); }
  };

  const handleAddLocation = async () => {
    if (!newLocName.trim()) { toast.error('Location name required'); return; }
    setAddingLocation(true);
    try {
      await axios.post(`${API}/locations`, { name: newLocName.trim() });
      toast.success(`“${newLocName.trim()}” added — $${billing?.additional_location_price || 29}/month added to your subscription.`);
      setNewLocName(''); setShowAddLocation(false);
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add location'); }
    finally { setAddingLocation(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="text-blue-600 animate-spin" /></div>;

  const addLocs = Math.max(0, locations.length - (billing?.included_locations || 1));
  const locPrice = billing?.additional_location_price || 29;
  const monthly = 249 + (addLocs * locPrice);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <CreditCard size={20} className="text-blue-600" /> Subscription & Billing
        </h1>
        <p className="text-slate-500 text-sm mt-1">Manage your plan, locations, and billing.</p>
      </div>

      {/* Plan */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading">Current Plan</CardTitle>
            <Badge className={`text-xs border ${STATUS_STYLES[billing?.subscription_status] || 'bg-slate-100 text-slate-600'}`}>
              {billing?.subscription_status?.replace('_', ' ')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-600">Base plan (1 location included)</span><span className="font-semibold text-slate-800">$249/month</span></div>
            {addLocs > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">{addLocs} additional location{addLocs !== 1 ? 's' : ''} × ${locPrice}</span><span className="font-semibold text-slate-800">+${(addLocs * locPrice).toLocaleString()}/month</span></div>}
            {billing?.billing_override && <div className="flex justify-between text-sm text-green-600"><span>🎉 Custom arrangement</span><span className="font-medium">{billing.billing_override.note || 'Special pricing'}</span></div>}
            <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold">
              <span className="text-slate-700">Estimated monthly</span>
              <span className="text-slate-900 text-base">${monthly.toLocaleString()}/month</span>
            </div>
          </div>
          {billing?.activated_at && <p className="text-xs text-slate-400">Active since {new Date(billing.activated_at).toLocaleDateString()}</p>}
          <div className="flex gap-2 flex-wrap">
            {billing?.subscription_status === 'active' && (
              <Button onClick={openPortal} disabled={portalLoading} variant="outline" className="gap-2">
                {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} Manage Billing in Stripe
              </Button>
            )}
            {billing?.subscription_status === 'pending_payment' && (
              <Button onClick={() => navigate('/checkout/resume')} className="gap-2"><CreditCard size={14} /> Complete Payment</Button>
            )}
            {billing?.subscription_status === 'past_due' && (
              <Button onClick={openPortal} className="gap-2 bg-orange-600 hover:bg-orange-500 text-white">
                <AlertTriangle size={14} /> Fix Payment Method
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Locations */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2"><MapPin size={15} className="text-blue-600" /> Locations</CardTitle>
            <span className="text-xs text-slate-500">{locations.length} active · 1 included · +$29/mo each extra</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {locations.map((loc, i) => (
            <div key={loc.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white" style={{ background: i === 0 ? '#1D4ED8' : '#6B7280' }}>{i + 1}</div>
                <span className="text-sm font-medium text-slate-800">{loc.name}</span>
              </div>
              <span className="text-xs font-semibold" style={{ color: i === 0 ? '#16A34A' : '#64748B' }}>{i === 0 ? 'Included' : `+$${locPrice}/mo`}</span>
            </div>
          ))}
          {!showAddLocation ? (
            <button onClick={() => setShowAddLocation(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
              <Plus size={14} /> Add Location (+${locPrice}/mo)
            </button>
          ) : (
            <div className="p-4 rounded-xl border-2 border-blue-200 bg-blue-50 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">Adding this location will automatically add <strong>${locPrice}/month</strong> to your Stripe subscription.</p>
              </div>
              <Input value={newLocName} onChange={e => setNewLocName(e.target.value)} placeholder="e.g. Westside Clinic" className="h-9"
                onKeyDown={e => { if (e.key === 'Enter') handleAddLocation(); if (e.key === 'Escape') setShowAddLocation(false); }} />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddLocation} disabled={addingLocation} className="gap-1.5">
                  {addingLocation ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                  {addingLocation ? 'Adding…' : `Confirm & Add (+$${locPrice}/mo)`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowAddLocation(false); setNewLocName(''); }}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Promo Code */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2"><Tag size={15} className="text-blue-600" /> Promotion Code</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-600">Promotion codes are applied during checkout or through the Stripe billing portal.</p>
          <Button variant="outline" className="gap-2" onClick={openPortal} disabled={portalLoading}>
            <ExternalLink size={14} /> Apply Promo Code in Stripe Portal
          </Button>
          <p className="text-xs text-slate-400">Codes are applied in Stripe’s secure environment.</p>
        </CardContent>
      </Card>
    </div>
  );
}
